<template>
  <SatellitesPage>
    <template v-if="platforms.length" #actions>
      <button class="btn _bg-green sat-btn-inline" @click.prevent="openCreate()"><SatIcon name="plus" /> {{ $t("satellites.platforms.create") }}</button>
    </template>

    <div v-if="loading" class="d-flex justify-content-center py-5">
      <img :src="require('@/assets/images/rolling.svg')" alt="loader" class="loader" />
    </div>

    <template v-else>
      <p class="platforms-description">{{ $t("satellites.platforms.description") }}</p>
      <div class="sat-note mt-3">
        <SatIcon name="info" />
        <span>
          {{ $t("satellites.platforms.shared_note") }}
          <router-link :to="{ name: 'Platforms' }">{{ $t("satellites.platforms.go_platforms") }}</router-link>
        </span>
      </div>

      <div v-if="!platforms.length" class="sat-empty mt-4">
        <SatIcon name="grid" :size="32" />
        <span class="sat-empty__title">{{ $t("satellites.platforms.empty_title") }}</span>
        <span class="sat-empty__text">{{ $t("satellites.platforms.empty_description") }}</span>
        <button class="btn _bg-green" @click.prevent="openCreate()">{{ $t("satellites.platforms.create") }}</button>
      </div>

      <div v-else class="c-table mt-4">
        <div class="inner">
          <vue-good-table styleClass="table  _no-wrap" :columns="columns" :rows="platforms">
            <template slot="table-column" slot-scope="props">
              <span v-show="props.column.field !== 'control'">{{ $t(props.column.label) }}</span>
            </template>

            <template slot="table-row" slot-scope="props">
              <template v-if="props.column.field === 'name'">
                <router-link :to="cardRoute(props.row)" class="platforms-name">{{ props.row.name }}</router-link>
                <div v-if="props.row.url" class="platforms-muted platforms-url">{{ props.row.url }}</div>
              </template>
              <template v-else-if="props.column.field === 'created_at'">
                <span v-if="props.row.created_at">{{ formatDate(props.row.created_at * 1000) }}</span>
                <span v-else class="platforms-muted">—</span>
              </template>
              <template v-else-if="props.column.field === 'control'">
                <div class="controls-wrapper">
                  <router-link :to="cardRoute(props.row)" class="sat-icon-btn" :title="$t('platform.card.open')" :aria-label="$t('platform.card.open')">
                    <SatIcon name="sliders" />
                  </router-link>
                </div>
              </template>
              <template v-else>{{ props.formattedRow[props.column.field] }}</template>
            </template>
            <div class="d-flex justify-content-center" slot="emptystate">
              {{ $t("satellites.platforms.empty_title") }}
            </div>
          </vue-good-table>
        </div>
      </div>
    </template>

    <modal @closed="resetCreate" :width="560" name="satellitePlatformCreate" height="auto" :scrollable="true" classes="creative-modal overflow-removed">
      <div class="pt-3 pb-4 px-4">
        <svg v-svg @click="$modal.hide('satellitePlatformCreate')" class="close" symbol="modal-close" size="0 0 15 15" role="info"></svg>
        <h3 class="modal-title pt-3">{{ $t("satellites.platforms.create_title") }}</h3>

        <form class="mt-3" @submit.prevent="create">
          <div class="sat-field">
            <label class="sat-field__label" for="satellite-platform-name">{{ $t("satellites.platforms.name") }}</label>
            <input id="satellite-platform-name" ref="name" v-model="name" type="text" :maxlength="nameMaxLength" :class="{ invalid: !!nameError }" @input="clearViolation('name')" />
            <div v-if="nameError" class="sat-field__error">{{ nameError }}</div>
          </div>

          <div class="sat-field">
            <div class="sat-field__label">{{ $t("satellites.platforms.category") }}</div>
            <div class="platforms-modes">
              <button
                v-for="category in categories"
                :key="category.value"
                type="button"
                class="platforms-mode"
                :class="{ _active: categoryId === category.value }"
                @click.prevent="setCategory(category.value)"
              >
                {{ category.title }}
              </button>
            </div>
            <div v-if="categoryError" class="sat-field__error">{{ categoryError }}</div>
            <div v-else class="sat-field__hint">{{ $t("satellites.platforms.category_hint") }}</div>
          </div>

          <div class="sat-note mt-3">
            <SatIcon name="info" />
            <span>{{ $t("satellites.platforms.create_note") }}</span>
          </div>

          <div v-if="createError" class="sat-field__error mt-3">{{ createError }}</div>

          <div class="text-center">
            <button type="button" class="btn mt-4 mr-3 _bg-gray" @click="$modal.hide('satellitePlatformCreate')">{{ $t("cancel") }}</button>
            <button type="submit" class="btn mt-4 _bg-green" :disabled="saving || !canCreate">{{ $t("satellites.platforms.create") }}</button>
          </div>
        </form>
      </div>
    </modal>
  </SatellitesPage>
</template>

<script>
import satelliteService from "@/api/satelliteService";
import SatellitesPage from "@/components/Satellites/SatellitesPage.vue";
import SatIcon from "@/components/Satellites/SatIcon.vue";
import satelliteFeatureGuard from "@/components/Satellites/satelliteFeatureGuard";
import { formatMixin, isOk, messagesOf } from "@/components/Satellites/format";

// Same as the platform form
const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 52;

export default {
  name: "SatellitePlatforms",
  components: { SatellitesPage, SatIcon },
  mixins: [formatMixin],
  beforeRouteEnter: satelliteFeatureGuard,
  data() {
    return {
      loading: true,
      platforms: [],
      saving: false,
      name: "",
      categoryId: null,
      violations: {},
      createError: "",
      nameMaxLength: NAME_MAX_LENGTH,
      // Values of the platform form: the category can't be changed later
      categories: [
        { title: "Adult", value: 1 },
        { title: "Mainstream", value: 2 },
      ],
      columns: [
        { label: "satellites.platforms.columns.name", field: "name", sortable: false },
        { label: "satellites.platforms.columns.id", field: "id", sortable: false },
        { label: "satellites.platforms.columns.created_at", field: "created_at", sortable: false },
        { label: "", field: "control", sortable: false },
      ],
    };
  },
  computed: {
    nameError() {
      return messagesOf(this.violations.name);
    },
    categoryError() {
      return messagesOf(this.violations.category_id);
    },
    canCreate() {
      return this.name.trim().length >= NAME_MIN_LENGTH && this.categoryId !== null;
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    // The card of the platform in the platforms section
    cardRoute(platform) {
      return { name: "PlatformCard", params: { id: platform.id, tab: "general" } };
    },
    fetch() {
      return satelliteService
        .getPlatforms()
        .then((res) => {
          if (isOk(res)) {
            this.platforms = (res.data && Array.isArray(res.data.platforms) && res.data.platforms) || [];
          } else if (res && res.status === 403) {
            this.$router.replace({ name: "Dashboard" }).catch(() => {});
          } else {
            this.showError(res);
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCreate() {
      this.resetCreate();
      this.$modal.show("satellitePlatformCreate");
      this.$nextTick(() => this.$refs.name && this.$refs.name.focus());
    },
    resetCreate() {
      this.name = "";
      this.categoryId = null;
      this.violations = {};
      this.createError = "";
    },
    setCategory(value) {
      this.categoryId = value;
      this.clearViolation("category_id");
    },
    clearViolation(key) {
      if (this.violations[key]) this.$delete(this.violations, key);
      this.createError = "";
    },
    create() {
      if (this.saving || !this.canCreate) return;
      this.saving = true;
      this.violations = {};
      this.createError = "";
      satelliteService
        .createPlatform({ name: this.name.trim(), category_id: this.categoryId })
        .then((res) => {
          if (isOk(res)) {
            this.$alert({ type: "success", title: this.$t("satellites.platforms.created") });
            this.$modal.hide("satellitePlatformCreate");
            this.fetch();
          } else if (res && res.status === 422 && res.data && res.data.violations) {
            this.violations = res.data.violations;
            if (!this.nameError && !this.categoryError) this.createError = res.message || this.$t("server_error");
          } else {
            this.createError = (res && res.message) || this.$t("server_error");
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.platforms-description {
  max-width: 720px;
  font-size: 13px;
  color: #9b9ba4;
}

.platforms-name {
  display: block;
  color: var(--sat-accent);
  font-weight: 600;
  white-space: normal;
  word-break: break-word;
}

.platforms-url {
  font-size: 12px;
  word-break: break-all;
  white-space: normal;
}

.platforms-muted {
  color: #9b9ba4;
  font-size: 13px;
}

.sat-empty > .sat-icon {
  color: var(--sat-accent);
}

.platforms-modes {
  display: inline-flex;
  flex-wrap: wrap;
  padding: 3px;
  border-radius: 10px;
  background: #f1f1f4;
}

.platforms-mode {
  padding: 6px 14px;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #6f6f78;
  background: transparent;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &._active {
    color: #317475;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
}

.controls-wrapper {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

h3.modal-title {
  max-width: 80%;
  text-align: center;
  margin: 0 auto;
  word-break: break-word;
}

body.dark {
  .platforms-modes {
    background: #2f2f35;
  }

  .platforms-mode {
    color: #b5b5bd;

    &._active {
      color: #6fc3c4;
      background: #151515;
    }
  }

  h3.modal-title {
    color: #dddddd;
  }
}
</style>
